<template>
    <div class="page">
        <EntityLandingHeader
            v-if="league"
            :backgroundImageUrl="`${$store.state.imagesCdn}/${league.image}`"
            :entity="league"
        />

        <section
            class="local-performers-section"
            v-if="localPerformers.length > 0"
        >
            <div class="local-performer-filter-container">
                <h2 class="local-performer-filter-header">Teams Near</h2>
                <section class="local-performer-filter">
                    <LocationFilter
                        :default-location="$config.defaultLocation ? $config.defaultLocation : userLocation"
                        :location="location" :locationFilterCopy="''" @error="onLocationError"
                        @select="onLocationSelect"
                    />
                </section>
            </div>

            <ul class="local-performers">
                <router-link
                    class="local-performer-container"
                    tag="li"
                    v-for="localPerformer in localPerformers"
                    :key="localPerformer.id" :to="`/performers/${localPerformer.id}`"
                >
                    <div class="local-performer">
                        <div
                            class="local-performer__image"
                            v-if="((localPerformer.images || {})[2] || {}).image"
                            :style="{ 'backgroundImage': `url(${localPerformer.images[2].image})` }"
                        />
                        <div
                            v-else
                            class="local-performer__image"
                            :style="{ 'backgroundImage': `url(${$store.state.imagesCdn}/${league.image})` }"
                        />
                        <div
                            :style="{ 'background-color': localPerformer.primaryColor }"
                            class="local-performer__image-text-container"
                        >
                            <div class="local-performer__image-team-text">{{ localPerformer.name }}</div>
                        </div>
                    </div>
                </router-link>
            </ul>
        </section>

        <section class="team-navigation">
            <h2
                class="team-navigation__header"
                :class="{ 'team-navigation__header--hide': localPerformers.length === 0 }"
            >
                Shop By Team
            </h2>
            <ul class="team-navigation__teams">
                <li
                    class="team-navigation__team"
                    v-for="team in league.children"
                    :key="team.slug"
                    @click="onMobilePerformerSelected(team.name, team.slug)"
                >
                    <router-link
                        tag="button"
                        class=team-icon-wrapper
                        :style="{'background': team.primaryColor}"
                        :to="team.slug"
                        @click.native="onPerformerSelected(team.name)"
                    >
                        <div
                            class="team-icon-text"
                            :style="{'color': team.secondaryColor}"
                        >
                            {{team.teamCode}}
                        </div>

                    </router-link>
                    <router-link
                        class="team-nagivation__team-name"
                        :to="team.slug"
                        @click.native="onPerformerSelected(team.name)"
                    >
                        {{ team.name }}
                    </router-link>
                    <span class="icon-chevron-lg-right"></span>
                </li>
            </ul>
        </section>
    </div>
</template>

<script>
import { EVENT_NAME, LEAGUE_LANDING_PAGE } from 'core/constants/googleAnalyticEvents'
import DateUtil from 'core/utilities/DateUtil'
import EntityLandingHeader from '../components/EntityLandingHeader.vue'
import errorHandleable from '../mixins/ErrorHandleable'
import eventFilterable from '../mixins/EventFilterable'
import eventListable from '../mixins/EventListable'
import LocationFilter from '../components/LocationFilter.vue'
import loyaltyFormattable from '../mixins/LoyaltyFormattable'
import quebecUserModal from '../mixins/QuebecUserModal'
import teamColorMapable from '../mixins/TeamColorMapable'

export default {
    beforeCreate () {
        this.isMobile = this.$store.state.isMobile
    },
    components: {
        EntityLandingHeader,
        LocationFilter
    },
    computed: {
        htmlPageTitle () {
            return this.league ? `${this.league.name} tickets at ${this.$config.partnerName}` : `Performer tickets at ${this.$config.partnerName}`
        }
    },
    created () {
        this.league = this.$config.highlights
            .find(genre => genre.id === 'sports').children
            .find(league => league.name.toLowerCase() === this.leagueName.toLowerCase())
    },
    data () {
        return {
            league: null,
            localPerformers: [],
            userLocation: null
        }
    },
    head: {
        title () {
            return {
                inner: this.htmlPageTitle
            }
        }
    },
    methods: {
        fetchLocalPerformers () {
            if (!this.location) { return }

            return this.$store.state.userLocationPromise
                .then(() => {
                    return this.$marketplace.getLocalPerformers({
                        category: this.leagueName,
                        latitude: this.location ? this.location.latitude : null,
                        longitude: this.location ? this.location.longitude : null,
                        per_page: 999,
                        regionId: this.location ? this.location.id : null
                    })
                        .then(response => {
                            this.localPerformers = this.mapTeamColors(response, this.$config.highlights, this.leagueName)
                        })
                })
        },
        fetchEvents () {
            return this.$store.state.userLocationPromise
                .then(userLocation => {
                    this.userLocation = userLocation
                    return this.$marketplace.getLocalEvents({
                        all_in_price: this.$config.isAllInPricing ? 1 : 0,
                        date_end: this.serializedEndDate,
                        date_start: this.serializedStartDate,
                        ignore_location: this.location ? 0 : 1,
                        location_id: this.location ? this.location.id : null,
                        latitude: this.location ? this.location.latitude : null,
                        longitude: this.location ? this.location.longitude : null,
                        page: this.page,
                        per_page: 10,
                        sub_genre_id: this.league.sub_genre_id,
                        utc_offset: DateUtil.format(new Date(), 'Z')
                    })
                        .then(response => {
                            return {
                                events: response.results,
                                pagination: response.meta
                            }
                        })
                })
        },
        onLoadMoreEvents () {
            this.$googleTagManager.pushEvent(EVENT_NAME, LEAGUE_LANDING_PAGE.LOAD_MORE_EVENTS_SELECTED)
            this.loadMoreEvents()
        },
        onDateRangeInput (dateRange) {
            this.$googleTagManager.pushEvent(EVENT_NAME, LEAGUE_LANDING_PAGE.DATE_RANGE_FILTER_APPLIED)
            this.onDateRangeSelected(dateRange)
        },
        onEventClick () {
            this.$googleTagManager.pushEvent(EVENT_NAME, LEAGUE_LANDING_PAGE.EVENT_SELECTED)
        },
        onLocationSelect (location) {
            this.$googleTagManager.pushEvent(EVENT_NAME, LEAGUE_LANDING_PAGE.LOCATION_FILTER_CHANGED)
            this.onLocationSelected(location)
        },
        onMobilePerformerSelected (teamName, teamSlug) {
            if (this.isMobile) {
                this.onPerformerSelected()
                this.$router.push(teamSlug)
            }
        },
        onPerformerSelected (teamName) {
            this.$googleTagManager.pushEvent(EVENT_NAME, Object.assign({}, LEAGUE_LANDING_PAGE.PEFORMER_NAME_SELECTED, { value: teamName }))
        }
    },
    mixins: [
        errorHandleable,
        eventFilterable,
        eventListable,
        loyaltyFormattable,
        quebecUserModal,
        teamColorMapable
    ],
    props: ['leagueName']
}

</script>

<style lang="postcss" scoped>
@import 'theme';

.page {
    margin-top: -65px;
    padding: 0 var(--site-padding) 48px;
    width: 100%;

    @media (--mq-full-min) {
        margin: -100px auto 0;
        width: var(--site-container-width);
    }
}

.filter {
    padding: 0;

    &.filter--date {
        margin-left: 12px;
        padding-left: 24px;
    }

    & .fx-date-range {
        display: inline-block;
    }

    & >>> .fx-select-custom {
        padding: 0 14px 0 0;
    }

    & >>> .fx-selection {
        color: var(--c-accent-text);
        font-size: 46px !important;
        text-decoration: underline;
    }

    & >>> .icon-cancel {
        color: var(--c-accent-text);
        font-size: 14px;
        position: absolute;
        right: 0;
        top: 8px;
    }

    & >>> .fx-select-input {
        border-bottom: 0;
    }

    & >>> .icon-chevron-lg-down {
        color: var(--c-accent-text);
        font-size: 14px;
        position: absolute;
        right: 0;
        top: 7px;
    }
}

.team-navigation {

    & .team-navigation__header {
        color: #444649;
        font-size: 20px;
        font-weight: 400;
        margin: 29px 0 24px 0;
        padding-top: 0px;

        @media (--mq-large-min) {
            border-top: 1px solid #5D5F63;
            padding-top: 40px;
            font-size: 24px;
        }
    }

    & .team-navigation__header--hide {
        border-top: none;
    }

    & .team-navigation__team {
        -webkit-column-break-inside: avoid;
        align-content: center;
        align-items: center;
        border-bottom: 1px solid #EAEAEB;
        break-inside: avoid;
        display: flex;
        font-size: 16px;
        font-weight: 500;
        letter-spacing: 0.73px;
        margin: 0 -16px 0 -16px;
        padding: 12px 16px 12px 16px;
        page-break-inside: avoid;

        @media (--mq-large-min) {
            border: none;
        }

        &:first-child {
            border-top: 1px solid #EAEAEB;

            @media (--mq-large-min) {
                border-top: none;
            }
        }

        & .icon-chevron-lg-right {
            color: #444649;
            font-size: 14px;
            margin-left: auto;

            @media (--mq-large-min) {
                display: none;
                margin-left: 0;
                margin-right: auto;
                padding-left: 2px;
            }
        }

        & a {
            color: #444649;

            &.team-nagivation__team-name {
                font-weight: 600;

                &:hover {
                    border-bottom: 2px solid #444649;
                    text-decoration: none;

                    & + .icon-chevron-lg-right {
                        display: block;
                        position: relative;
                        top: 3px;
                    }
                }
            }
        }
    }

    @media (--mq-medium-min) {
        & .team-navigation__teams {
            column-count: 2;
            margin: 0;
        }
    }

    @media (--mq-large-min) {
        & .team-navigation__teams {
            column-count: 3;
            margin: 0;
        }
    }
}

.team-icon-wrapper {
    border-radius: 50%;
    border: none;
    font-size: 16px;
    font-weight: 600;
    line-height: 40px;
    margin-right: 8px;
    padding: 0;
    text-align: center;
    width: 40px;

    &:hover {
        cursor: pointer;
    }
}

.local-performer-filter-container {
    align-items: center;
    display: flex;
}

.local-performers {
    overflow: auto;
    white-space: nowrap;

    margin: 0 -16px 0 -16px;
    & .local-performer-container:first-child {
        margin-left: 16px;
    }

    @media (--mq-large-min) {
        overflow: visible;
        white-space: normal;
        margin: 0;
        & .local-performer-container:first-child {
            margin-left: 0;
        }
    }
}

.local-performer-container {
    border-radius: 8px;
    display: inline-block;
    height: 141px;
    margin-bottom: 10px;
    margin-right: 13px;
    width: 141px;

    @media (--mq-large-min) {
        &:active,
        &:focus,
        &:hover {
            box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
            cursor: pointer;
            transform: scale(1.02);
            transition: transform 0.2s ease;
        }
    }

    & .local-performer {
        border-radius: 8px;
        height: 100%;
        position: relative;
        transition: transform 0.2s ease;
        width: 100%;
    }

    & .local-performer__image {
        background-position: 50% 50%;
        background-repeat: no-repeat;
        background-size: cover;
        border-radius: 8px;
        height: 100%;
        width: 100%;
    }

    & .local-performer__image-text-container {
        align-items: center;
        border-radius: 0 0 8px 8px;
        clip-path: polygon(0% 17%, 100% 0%, 100% 100%, 0% 100%);
        color: white;
        display: flex;
        height: 60px;
        left: 50%;
        margin: 0;
        position: absolute;
        top: 79%;
        transform: translate(-50%, -50%);
        white-space: normal;
        width: 100%;
        z-index: 20;
    }

    & .local-performer__image-team-text {
        color: #FFF;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0;
        line-height: 17px;
        padding: 20px 8px 8px 8px;
    }
}

.local-performer-filter-header {
    color: #444649;
    font-size: 20px;
    font-weight: 400;
    margin: 14px 0 14px 0;
    padding-top: 0px;

    @media (--mq-large-min) {
        font-size: 24px;
    }
}

.local-performer-filter {
    margin-left: 11px;

    & >>> .location-filter .selection {
        & span {
            color: #444649;
            font-size: 20px;
            letter-spacing: 0;
            font-weight: 400;
            @media (--mq-large-min) {
                font-size: 24px;
            }
        }

        & .open-menu-icon {
            & .icon-chevron-lg-down {
                font-size: 15px;
                top: 2px;
            }
        }
    }

    & >>> .icon-location-marker {
        top: 2px;
        font-size: 18px;
        @media (--mq-large-min) {
            top: 6px;
        }
    }
}

.local-performers-section {
    background-color: #FFFFFF;
    margin: 0 -16px 0 -16px;
    padding: 0 16px 0 16px;

    @media (--mq-large-min) {
        margin: 0;
        background-color: initial;
        padding: 0;

    }
}

</style>
